import { defineMessages, useIntl } from 'react-intl-next';

import { SPACE_PERMISSIONS_SETTINGS } from '@confluence/named-routes/entry-points/routes.ts';
import { SpacePermissionsTabKey } from '@confluence/space-permissions/entry-points/spacePermissionsKeys.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';
import { useSpaceManagementOperations } from '@confluence/space-utils/entry-points/graphql.ts';

import type {
	SpaceSettingsWebItemSectionsFragment_webItemSections,
	SpaceSettingsWebItemSectionsFragment_webItemSections_items,
} from './__types__/SpaceSettingsWebItemSectionsFragment.ts';
import { SPACE_PERMISSIONS_WEBITEM_SECTION_ID } from './useSpacePermissionsWebItems.tsx';
import type { SpaceSettingsWebItemSectionsInjector } from './SpaceSettingsWebItemSectionsInjector.ts';

export const SPACE_PUBLIC_LINKS_WEBITEM_ID = 'space-public-links';
const SPACE_PERMISSIONS_COMPLETE_KEY = 'confluence-frontend.';

const i18n = defineMessages({
	spacePermissionsWebItemPublicLinksLink: {
		id: 'space-settings.web-item-section.space-permissions.web-item.public-links',
		defaultMessage: 'Public links',
		description: 'Label for the web item link for public links',
	},
});

const addPublicLinksWebItem = (
	publicLinksWebItem: SpaceSettingsWebItemSectionsFragment_webItemSections_items,
	webItemSections:
		| ReadonlyArray<SpaceSettingsWebItemSectionsFragment_webItemSections | null>
		| undefined,
) => {
	if (!publicLinksWebItem) {
		return webItemSections;
	}

	return webItemSections?.map((section) => {
		if (section?.id === SPACE_PERMISSIONS_WEBITEM_SECTION_ID) {
			// XXX Don't mutate the input webItemSections! May be shared across invocations.
			const [firstItem, ...restItems] = section.items;

			return {
				...section,
				items: [firstItem, publicLinksWebItem, ...restItems],
			};
		}
		return section;
	});
};

export const usePublicLinksWebItem: SpaceSettingsWebItemSectionsInjector = ({
	webItemSections,
}) => {
	const intl = useIntl();
	const spaceKey = useSpaceKey();
	const { isRbacEnabled } = useSpaceRoleMode();
	const { canManagePublicLinks } = useSpaceManagementOperations({
		spaceKey: spaceKey ?? '',
	});

	if (!spaceKey) {
		return webItemSections;
	}

	// When RBAC is enabled, hide public links nav item if user doesn't have permission
	if (isRbacEnabled && !canManagePublicLinks) {
		return webItemSections;
	}

	const publicLinksWebItem: SpaceSettingsWebItemSectionsFragment_webItemSections_items = {
		id: SPACE_PUBLIC_LINKS_WEBITEM_ID,
		label: intl.formatMessage(i18n.spacePermissionsWebItemPublicLinksLink),
		url: SPACE_PERMISSIONS_SETTINGS.toUrl({
			spaceKey,
			tab: SpacePermissionsTabKey.PublicLinks,
		}),
		completeKey: SPACE_PERMISSIONS_COMPLETE_KEY + SPACE_PUBLIC_LINKS_WEBITEM_ID,
		icon: null,
	};

	return addPublicLinksWebItem(publicLinksWebItem, webItemSections);
};
